import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, Col, Row, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileArrowUp,
  faPencil,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { TransactionModal } from "./TransactionModal";
import axios from "axios";
import config from "../../config.json";
import { SortableTH } from "../Global/SortableTH";
import { FilterBar } from "../Global/FilterBar";
import { TransactionUploadModal } from "./TransactionUploadModal";
import { PaginationBar } from "../Global/PaginationBar";
import { useCookies } from "react-cookie";
import { Loading } from "../Loading";

export const Transactions = () => {
  const [cookies] = useCookies(["user"]);
  const [showModal, setShowModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [tally, setTally] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [activeTransactionData, setActiveTransactionData] = useState({});
  const [sortField, setSortField] = useState("payment_date");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [query, setQuery] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  const addTransaction = () => {
    setActiveTransactionData({});
    setShowModal(true);
  };

  const editTransaction = (data) => {
    setActiveTransactionData({
      ...data,
    });
    setShowModal(true);
  };

  const updateQuery = (newQuery, newDateFrom, newDateTo) => {
    setQuery(newQuery);
    setDateFrom(newDateFrom);
    setDateTo(newDateTo);
  };

  const updatePagination = (newPageIndex, newPageSize) => {
    setPageIndex(newPageIndex);
    setPageSize(newPageSize);
  };

  const updateSortField = (field) => {
    if (field === sortField) {
      if (sortOrder === "ASC") setSortOrder("DESC");
      else setSortOrder("ASC");
    }

    setSortField(field);
  };

  useEffect(() => {
    setIsLoading(true);

    axios
      .get(
        `${
          config.api[window.location.hostname]
        }/transactionList.php?cache=${new Date().getTime()}&source=${
          cookies.user.clientMeta.tableTransactions
        }&query=${query}&dateFrom=${dateFrom}&dateTo=${dateTo}&sortField=${sortField}&sortOrder=${sortOrder}&pageIndex=${pageIndex}&pageSize=${pageSize}`
      )
      .then((response) => {
        setTally(response.data.tally);
        setTransactions(response.data.transactions);
        setIsLoading(false);
      });
  }, [
    cookies,
    dateFrom,
    dateTo,
    pageIndex,
    pageSize,
    query,
    sortField,
    sortOrder,
  ]);

  if (isLoading) return <Loading />;
  if (!transactions) return null;

  return (
    <>
      {!!cookies?.user?.meta?.transactionsCanCreate && (
        <TransactionModal
          data={activeTransactionData}
          setTransactions={setTransactions}
          setData={setActiveTransactionData}
          setIsLoading={setIsLoading}
          show={showModal}
          onHide={() => setShowModal(false)}
          /*--- Filters ---*/
          dateFrom={dateFrom}
          dateTo={dateTo}
          pageIndex={pageIndex}
          pageSize={pageSize}
          query={query}
          sortField={sortField}
          sortOrder={sortOrder}
        />
      )}

      {!!cookies?.user?.meta?.transactionsCanImport && (
        <TransactionUploadModal
          setIsLoading={setIsLoading}
          setTransactions={setTransactions}
          show={showUploadModal}
          onHide={() => setShowUploadModal(false)}
          /*--- Filters ---*/
          dateFrom={dateFrom}
          dateTo={dateTo}
          pageIndex={pageIndex}
          pageSize={pageSize}
          query={query}
          sortField={sortField}
          sortOrder={sortOrder}
        />
      )}

      <div>
        <Row className="alignItemsBaseline">
          <Col md={9} className="mb-3">
            <FilterBar
              dateFrom={dateFrom}
              dateTo={dateTo}
              query={query}
              onUpdateQuery={updateQuery}
            />
          </Col>
          <Col md={3} className="mb-3 text-end">
            <ButtonGroup>
              {!!cookies?.user?.meta?.transactionsCanImport && (
                <Button
                  variant="light"
                  onClick={() => setShowUploadModal(true)}
                >
                  <FontAwesomeIcon icon={faFileArrowUp} /> Upload
                </Button>
              )}

              {!!cookies?.user?.meta?.transactionsCanCreate && (
                <Button onClick={addTransaction}>
                  <FontAwesomeIcon icon={faPlus} inverse /> Add
                </Button>
              )}
            </ButtonGroup>
          </Col>
        </Row>
      </div>

      <Table bordered striped responsive>
        <thead>
          <tr>
            <SortableTH
              field="payment_date"
              onClick={updateSortField}
              sortField={sortField}
              sortOrder={sortOrder}
            >
              Date
            </SortableTH>
            <SortableTH
              field="source"
              onClick={updateSortField}
              sortField={sortField}
              sortOrder={sortOrder}
            >
              Source
            </SortableTH>
            <SortableTH
              field="message_id"
              onClick={updateSortField}
              sortField={sortField}
              sortOrder={sortOrder}
            >
              Message Id
            </SortableTH>
            <SortableTH
              field="donation_id"
              onClick={updateSortField}
              sortField={sortField}
              sortOrder={sortOrder}
            >
              Donation Id
            </SortableTH>
            <SortableTH
              field="amount"
              onClick={updateSortField}
              sortField={sortField}
              sortOrder={sortOrder}
            >
              Amount
            </SortableTH>
            <SortableTH
              field="recurring"
              onClick={updateSortField}
              sortField={sortField}
              sortOrder={sortOrder}
            >
              Recurring
            </SortableTH>
            <SortableTH
              field="donation_recurring"
              onClick={updateSortField}
              sortField={sortField}
              sortOrder={sortOrder}
            >
              Period
            </SortableTH>
            <SortableTH
              field="first_name"
              onClick={updateSortField}
              sortField={sortField}
              sortOrder={sortOrder}
            >
              First
            </SortableTH>
            <SortableTH
              field="last_name"
              onClick={updateSortField}
              sortField={sortField}
              sortOrder={sortOrder}
            >
              Last
            </SortableTH>
            <SortableTH
              field="billing_email"
              onClick={updateSortField}
              sortField={sortField}
              sortOrder={sortOrder}
            >
              Email
            </SortableTH>
            <SortableTH
              field="organization"
              onClick={updateSortField}
              sortField={sortField}
              sortOrder={sortOrder}
            >
              Org.
            </SortableTH>
            {!!cookies?.user?.meta?.transactionsCanEdit && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {(transactions ?? []).map((transaction) => (
            <tr key={transaction.id}>
              <td>{transaction.payment_date} </td>
              <td>{transaction.source} </td>
              <td>{transaction.message_id} </td>
              <td>{transaction.donation_id} </td>
              <td>{transaction.amount} </td>
              <td>{transaction.recurring} </td>
              <td>{transaction.donation_recurring} </td>
              <td>{transaction.first_name} </td>
              <td>{transaction.last_name} </td>
              <td>{transaction.billing_email} </td>
              <td>{transaction.organization} </td>
              {!!cookies?.user?.meta?.transactionsCanEdit && (
                <td className="text-end">
                  <Button
                    variant="dark"
                    onClick={() => editTransaction(transaction)}
                  >
                    <FontAwesomeIcon icon={faPencil} /> {}
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>

      <PaginationBar
        pageIndex={pageIndex}
        pageSize={pageSize}
        tally={tally}
        onUpdatePagination={updatePagination}
      />
    </>
  );
};
